/**
 * Applying a change to the message caches before the server has heard of it.
 *
 * Reactions and pins used to wait for the socket to echo them back, which on
 * a good connection is a tenth of a second and on a bad one is a second and
 * a half of a button that looks broken. The click now lands immediately and
 * the server's own copy overwrites it when it arrives.
 *
 * The patch runs across every cached message list rather than one known key,
 * because the same message can sit in a conversation, a party channel and a
 * search result at once, and all three should agree.
 */

import type { QueryClient } from '@tanstack/react-query';
import type { Message } from './types';

type Holder = { messages?: Message[] };

/** Rewrites one message wherever it is cached. */
export function patchMessage(
  qc: QueryClient,
  id: string,
  edit: (m: Message) => Message
): void {
  for (const cache of qc.getQueryCache().findAll()) {
    const data = cache.state.data as Holder | undefined;
    if (!data || !Array.isArray(data.messages)) continue;
    if (!data.messages.some((m) => m.id === id)) continue;

    qc.setQueryData(cache.queryKey, {
      ...data,
      messages: data.messages.map((m) => (m.id === id ? edit(m) : m))
    });
  }
}

/**
 * Toggling your own reaction.
 *
 * Mirrors what the server does - add if you are not on it, remove if you are
 * - so the count that appears under the message is the count that stays there
 * once the real one lands.
 */
export function toggleReaction(
  qc: QueryClient,
  id: string,
  emoji: string,
  meId: string | undefined
): void {
  if (!meId) return;

  patchMessage(qc, id, (m) => {
    const all = { ...(m.reactions ?? {}) };
    const on = all[emoji] ?? [];
    const next = on.includes(meId) ? on.filter((x) => x !== meId) : [...on, meId];

    // An emoji nobody is on should not linger as an empty chip.
    if (next.length) all[emoji] = next;
    else delete all[emoji];

    return { ...m, reactions: all };
  });
}

/**
 * A presence change, everywhere that person is drawn.
 *
 * The same face appears in the conversation list, the header, the details
 * panel, a party roster and a profile card, and each of those reads a
 * different query. Invalidating all of them would refetch five lists to change
 * one dot, so the change is written into whatever is already cached instead.
 *
 * Recursive because the shapes differ - a friend row is { user }, a thread is
 * { user, last }, a group is { members: [{ user }] } - and the one thing they
 * have in common is a PublicUser somewhere inside. Depth is capped so a cache
 * entry that happens to be deeply nested cannot turn one socket event into a
 * long walk.
 */
export function patchPresence(
  qc: QueryClient,
  change: { userId: string; status?: string; customStatus?: string; playing?: unknown }
): void {
  const isUser = (v: unknown): v is Record<string, unknown> =>
    !!v && typeof v === 'object' && (v as Record<string, unknown>).id === change.userId
    && 'status' in (v as Record<string, unknown>);

  /* Returns a new value when something changed, and the value it was given
     when nothing did - so an untouched cache entry is not replaced, and React
     Query does not re-render every screen on every presence event. */
  function walk(value: unknown, depth: number): unknown {
    if (depth > 6 || !value || typeof value !== 'object') return value;

    if (Array.isArray(value)) {
      let hit = false;
      const next = value.map((v) => { const w = walk(v, depth + 1); if (w !== v) hit = true; return w; });
      return hit ? next : value;
    }

    if (isUser(value)) {
      return {
        ...value,
        status: change.status ?? value.status,
        customStatus: change.customStatus ?? value.customStatus,
        playing: change.playing !== undefined ? change.playing : value.playing
      };
    }

    let hit = false;
    const out: Record<string, unknown> = { ...(value as Record<string, unknown>) };
    for (const [k, v] of Object.entries(out)) {
      const w = walk(v, depth + 1);
      if (w !== v) { out[k] = w; hit = true; }
    }
    return hit ? out : value;
  }

  for (const cache of qc.getQueryCache().findAll()) {
    const data = cache.state.data;
    const next = walk(data, 0);
    if (next !== data) qc.setQueryData(cache.queryKey, next);
  }
}
